import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Briefcase, ArrowLeft, Loader2, AlertCircle, Upload, X, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewJob = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    salary_min: '',
    salary_max: '',
    salary_type: 'monthly',
    employment_type: 'full_time',
    experience_required: '',
    contact_phone: user?.phone || '',
    contact_email: user?.email || '',
  });

  useEffect(() => {
    if (!user) {
      toast.error('Zaloguj się, aby dodać ofertę pracy');
      navigate('/login?redirect=/jobs/new');
      return;
    }
    
    const fetchMeta = async () => {
      try {
        const [catRes, empRes] = await Promise.all([
          fetch(`${API}/jobs/categories`),
          fetch(`${API}/jobs/employment-types`)
        ]);
        if (catRes.ok) setCategories(await catRes.json());
        if (empRes.ok) setEmploymentTypes(await empRes.json());
      } catch (err) {
        console.error('Error:', err);
      }
    };
    fetchMeta();
  }, [user, navigate]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (images.length + files.length > 3) {
      toast.error('Maksymalnie 3 zdjęcia');
      return;
    }

    setUploadingImages(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`Plik ${file.name} jest za duży (max 5MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API}/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setImages(prev => [...prev, data.url]);
        } else {
          toast.error(`Błąd przesyłania ${file.name}`);
        }
      }
    } catch (err) {
      toast.error('Błąd przesyłania zdjęć');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.location || !formData.category) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }

    if (!formData.salary_min || isNaN(parseFloat(formData.salary_min)) || parseFloat(formData.salary_min) <= 0) {
      toast.error('Podaj prawidłową stawkę minimalną (tylko cyfry)');
      return;
    }

    if (formData.salary_max && parseFloat(formData.salary_max) < parseFloat(formData.salary_min)) {
      toast.error('Stawka maksymalna nie może być niższa od minimalnej');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          salary_min: parseFloat(formData.salary_min),
          salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
          images: images,
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Oferta pracy dodana!');
        navigate(`/jobs/${data.job_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Błąd podczas dodawania');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="new-job-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/jobs')} 
            className="text-violet-100 hover:text-white hover:bg-violet-500/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Powrót
          </Button>
          
          <div className="flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-white" />
            <h1 className="font-heading font-bold text-2xl text-white">Dodaj ofertę pracy</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Szczegóły oferty</CardTitle>
            <CardDescription>Opisz stanowisko, które oferujesz</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title">Tytuł stanowiska *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="np. Murarz - praca na budowie"
                  required
                  data-testid="input-title"
                />
              </div>

              {/* Category */}
              <div>
                <Label>Kategoria stanowiska *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Wybierz kategorię" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="location">Miejsce pracy *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="np. Warszawa, ul. Budowlana 15"
                  required
                  data-testid="input-location"
                />
              </div>

              {/* Salary - REQUIRED */}
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <Label className="text-yellow-800 font-semibold">Stawka (wymagane) *</Label>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="salary_min" className="text-sm">Minimalna (PLN) *</Label>
                    <Input
                      id="salary_min"
                      name="salary_min"
                      type="number"
                      min="1"
                      step="1"
                      value={formData.salary_min}
                      onChange={handleChange}
                      placeholder="np. 5000"
                      required
                      data-testid="input-salary-min"
                    />
                  </div>
                  <div>
                    <Label htmlFor="salary_max" className="text-sm">Maksymalna (PLN)</Label>
                    <Input
                      id="salary_max"
                      name="salary_max"
                      type="number"
                      min="1"
                      step="1"
                      value={formData.salary_max}
                      onChange={handleChange}
                      placeholder="np. 7000"
                      data-testid="input-salary-max"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Typ stawki</Label>
                    <Select value={formData.salary_type} onValueChange={(v) => setFormData(prev => ({ ...prev, salary_type: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Za godzinę</SelectItem>
                        <SelectItem value="daily">Za dzień</SelectItem>
                        <SelectItem value="monthly">Miesięcznie</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Employment Type */}
              <div>
                <Label>Typ zatrudnienia</Label>
                <Select value={formData.employment_type} onValueChange={(v) => setFormData(prev => ({ ...prev, employment_type: v }))}>
                  <SelectTrigger data-testid="select-employment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {employmentTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Opis stanowiska *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Opisz zakres obowiązków, wymagania, co oferujesz..."
                  rows={6}
                  required
                  data-testid="input-description"
                />
              </div>

              {/* Images */}
              <div>
                <Label>Zdjęcia miejsca pracy (opcjonalne, max 3)</Label>
                <div className="mt-2">
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-4">
                      {images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt={`Zdjęcie ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {images.length < 3 && (
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-violet-500 hover:bg-violet-50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImages}
                      />
                      {uploadingImages ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                          <span className="text-slate-600">Przesyłanie...</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-5 h-5 text-slate-400" />
                          <span className="text-slate-600">Dodaj zdjęcia miejsca pracy</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              {/* Experience */}
              <div>
                <Label htmlFor="experience_required">Wymagane doświadczenie</Label>
                <Input
                  id="experience_required"
                  name="experience_required"
                  value={formData.experience_required}
                  onChange={handleChange}
                  placeholder="np. Min. 2 lata doświadczenia"
                  data-testid="input-experience"
                />
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_phone">Telefon kontaktowy</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="np. 600 700 800"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Email kontaktowy</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/jobs')}>
                  Anuluj
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-violet-500 hover:bg-violet-600"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Dodawanie...
                    </>
                  ) : (
                    'Dodaj ofertę pracy'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewJob;
